/**
 * In-place markdown editor for a Sentinel response (administrators only).
 * Reuses the shared ProseMirror message editor, so what the admin types
 * round-trips through the same markdown serializer Kim's drafts use.
 */

import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormatToolbar } from "@/components/message-editor/format-toolbar";
import { useMessageEditor } from "@/components/message-editor/use-message-editor";
import "@/components/message-editor/editor.css";

interface SentinelResponseEditorProps {
  messageId: number;
  initialMarkdown: string;
  isSaving: boolean;
  onSave: (markdown: string) => void;
  onCancel: () => void;
}

export function SentinelResponseEditor({
  messageId,
  initialMarkdown,
  isSaving,
  onSave,
  onCancel,
}: SentinelResponseEditorProps) {
  const editor = useMessageEditor({
    initialMarkdown,
    editorKey: `sentinel-response-${messageId}`,
    placeholder: "Kim's response",
  });

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={editor.hostRef}
        className="message-editor rounded-md border border-border bg-background px-3 py-2"
        aria-label="Edit Kim's response"
      />
      <div className="flex items-center justify-between gap-2">
        <FormatToolbar editor={editor} />
        <div className="flex items-center gap-1.5">
          <Button type="button" variant="outline" size="sm" disabled={isSaving} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isSaving}
            onClick={() => {
              const markdown = editor.getMarkdown().trim();
              if (markdown) onSave(markdown);
            }}
          >
            {isSaving && <Loader2 className="size-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
